import type { LeaderboardEntry } from '@/types/leaderboard';
import { SCORING } from '@/config/scoring';

const STORAGE_KEY = 'packman_leaderboard';

function readEntries(): LeaderboardEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isValidEntry);
  } catch {
    return [];
  }
}

function isValidEntry(entry: unknown): entry is LeaderboardEntry {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const obj = entry as Record<string, unknown>;
  return (
    typeof obj['id'] === 'string' &&
    typeof obj['initials'] === 'string' &&
    typeof obj['score'] === 'number' &&
    typeof obj['levelReached'] === 'number' &&
    typeof obj['date'] === 'string'
  );
}

function writeEntries(entries: ReadonlyArray<LeaderboardEntry>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

export function getTopScores(limit: number): LeaderboardEntry[] {
  const entries = readEntries();
  entries.sort((a, b) => b.score - a.score);
  return entries.slice(0, limit);
}

export function addScore(entry: LeaderboardEntry): void {
  const entries = readEntries();
  entries.push(entry);
  entries.sort((a, b) => b.score - a.score);
  const trimmed = entries.slice(0, SCORING.MAX_LEADERBOARD_ENTRIES);
  writeEntries(trimmed);
}

export function isQualifyingScore(score: number): boolean {
  const entries = readEntries();
  if (entries.length < SCORING.MAX_LEADERBOARD_ENTRIES) {
    return true;
  }
  const sorted = entries.sort((a, b) => b.score - a.score);
  const lowestQualifying = sorted[SCORING.MAX_LEADERBOARD_ENTRIES - 1];
  return lowestQualifying !== undefined && score > lowestQualifying.score;
}
